import { useState, useEffect, useRef } from 'react'

const PHASES = [
  {
    id: 'understand',
    icon: '🧠',
    label: 'Understanding Request',
    color: '#a78bfa',
    lines: [
      { text: '✓ Goal detected', delay: 300 },
      { text: '✓ Backend feature request', delay: 600 },
      { text: '✓ Estimated difficulty: Easy', delay: 900 },
    ],
  },
  {
    id: 'memory',
    icon: '🧠',
    label: 'Loading Memory',
    color: '#7c6ef5',
    lines: [
      { text: '✓ Project: Task Manager', delay: 200 },
      { text: '✓ Stack: React + Express + PostgreSQL', delay: 450 },
      { text: '✓ Style: TypeScript', delay: 700 },
      { text: '✓ Previous: Authentication system', delay: 950 },
    ],
  },
  {
    id: 'skills',
    icon: '🧠',
    label: 'Matching Skills',
    color: '#818cf8',
    lines: [
      { text: '✓ Skill: "Create Express API Route"', delay: 300 },
      { text: '✓ Confidence: 92%', delay: 600 },
    ],
  },
  {
    id: 'permissions',
    icon: '🔒',
    label: 'Checking Permissions',
    color: '#f5a157',
    lines: [
      { text: '✓ Read files', delay: 200 },
      { text: '✓ Write files', delay: 400 },
      { text: '✓ Run terminal', delay: 600 },
    ],
  },
  {
    id: 'discovery',
    icon: '📂',
    label: 'Scanning Project',
    color: '#38bdf8',
    terminal: true,
    cmd: '> Get-ChildItem -Recurse package.json',
    lines: [
      { text: '✓ package.json', delay: 400 },
      { text: '✓ server/index.cjs', delay: 700 },
      { text: '✓ server/routes/tasks.js', delay: 1000 },
      { text: 'Framework: Express  Port: 5176', delay: 1300, dim: true },
    ],
  },
  {
    id: 'reading',
    icon: '📄',
    label: 'Reading Files',
    color: '#5fd4a4',
    lines: [
      { text: '✓ package.json', delay: 200 },
      { text: '✓ server/index.cjs', delay: 450 },
      { text: '✓ server/routes/tasks.js', delay: 700 },
      { text: '3 files · 1,247 lines loaded', delay: 950, dim: true },
    ],
  },
  {
    id: 'analyze',
    icon: '🔍',
    label: 'Analyzing Codebase',
    color: '#7c6ef5',
    lines: [
      { text: '✓ Express server found', delay: 300 },
      { text: '✓ Existing routes detected', delay: 600 },
      { text: '✓ /api/health endpoint missing', delay: 900 },
      { text: 'Risk: LOW  Affected: server/index.cjs', delay: 1200, dim: true },
    ],
  },
  {
    id: 'plan',
    icon: '📋',
    label: 'Creating Execution Plan',
    color: '#f5a157',
    lines: [
      { text: '1. Add GET /api/health', delay: 300 },
      { text: '2. Return status payload', delay: 600 },
      { text: '3. Test endpoint', delay: 900 },
      { text: '4. Verify response  Confidence: 97%', delay: 1200, dim: true },
    ],
  },
  {
    id: 'approval',
    icon: '✋',
    label: 'Waiting for Approval',
    color: '#f07070',
    approval: true,
    lines: [
      { text: 'server/index.cjs', delay: 300 },
    ],
  },
  {
    id: 'apply',
    icon: '✏️',
    label: 'Applying Changes',
    color: '#5fd4a4',
    diff: true,
    lines: [
      { text: '+ app.get(\'/api/health\', (req,res) => {', delay: 300, add: true },
      { text: '+   res.json({ status: "healthy" })', delay: 600, add: true },
      { text: '+ })', delay: 900, add: true },
      { text: '✓ Changes written', delay: 1200 },
    ],
  },
  {
    id: 'test',
    icon: '🧪',
    label: 'Running Tests',
    color: '#38bdf8',
    terminal: true,
    cmd: '> npm run dev',
    lines: [
      { text: 'PID: 19444', delay: 400, dim: true },
      { text: 'Waiting for startup...', delay: 700, dim: true },
      { text: '✓ Server online :5176', delay: 1400 },
    ],
  },
  {
    id: 'verify',
    icon: '✅',
    label: 'Verifying Solution',
    color: '#5fd4a4',
    terminal: true,
    cmd: '> curl localhost:5176/api/health',
    lines: [
      { text: '200 OK', delay: 600 },
      { text: '{ "status": "healthy" }', delay: 900, dim: true },
      { text: '✓ Verification passed', delay: 1200 },
    ],
  },
  {
    id: 'cleanup',
    icon: '🧹',
    label: 'Stopping Processes',
    color: '#a78bfa',
    terminal: true,
    cmd: '> Stop-Process 19444',
    lines: [
      { text: '✓ PID 19444 terminated', delay: 500 },
    ],
  },
  {
    id: 'summary',
    icon: '🎉',
    label: 'Complete',
    color: '#5fd4a4',
    summary: true,
    lines: [
      { text: 'Files: server/index.cjs', delay: 200 },
      { text: 'Commands: npm run dev, curl', delay: 450 },
      { text: 'Duration: 11.2s', delay: 700 },
      { text: '✓ Health endpoint added', delay: 950 },
    ],
  },
  {
    id: 'resume',
    icon: '💾',
    label: 'Session Saved',
    color: '#7c6ef5',
    lines: [
      { text: 'Run ID: run_abc123', delay: 300, dim: true },
      { text: '✓ Resume available', delay: 600 },
    ],
  },
]

// Single phase card that animates its lines in
function PhaseCard({ phase, active, done, onLineReveal }) {
  const [visibleLines, setVisibleLines] = useState([])

  useEffect(() => {
    if (!active) return
    setVisibleLines([])
    const timers = phase.lines.map((line, i) =>
      setTimeout(() => {
        setVisibleLines(prev => [...prev, i])
        if (onLineReveal) onLineReveal()
      }, line.delay)
    )
    return () => timers.forEach(clearTimeout)
  }, [active, phase])

  const borderColor = done ? `${phase.color}30` : active ? `${phase.color}55` : 'rgba(255,255,255,0.05)'
  const bg = done ? 'rgba(255,255,255,0.015)' : active ? `${phase.color}08` : 'transparent'

  return (
    <div style={{
      borderRadius: 12,
      border: `1px solid ${borderColor}`,
      background: bg,
      padding: '12px 16px',
      transition: 'all 0.3s',
      marginBottom: 6,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: (active || done) && phase.lines.length ? 10 : 0 }}>
        <span style={{ fontSize: 13 }}>{phase.icon}</span>
        <span style={{
          fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
          color: done ? 'rgba(255,255,255,0.5)' : active ? '#fff' : 'rgba(255,255,255,0.3)',
        }}>{phase.label}</span>
        <div style={{ flex: 1 }} />
        {done && <span style={{ fontSize: 10, color: '#5fd4a4', fontWeight: 700 }}>✓</span>}
        {active && (
          <div style={{ display: 'flex', gap: 3 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 4, height: 4, borderRadius: '50%',
                background: phase.color,
                animation: `dot-pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
              }} />
            ))}
          </div>
        )}
      </div>

      {/* Lines */}
      {(active || done) && visibleLines.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Terminal command */}
          {phase.terminal && (
            <div style={{
              fontFamily: 'ui-monospace, monospace', fontSize: 10.5,
              color: phase.color, background: 'rgba(0,0,0,0.4)',
              borderRadius: 6, padding: '5px 10px', marginBottom: 4,
            }}>{phase.cmd}</div>
          )}

          {/* Diff header */}
          {phase.diff && (
            <div style={{
              fontFamily: 'ui-monospace, monospace', fontSize: 10,
              color: 'rgba(255,255,255,0.3)', marginBottom: 2,
            }}>server/index.cjs</div>
          )}

          {phase.lines.slice(0, visibleLines.length).map((line, i) => (
            <div key={i} style={{
              fontSize: 11,
              fontFamily: (phase.terminal || phase.diff) ? 'ui-monospace, monospace' : 'inherit',
              color: line.add ? '#5fd4a4' : line.dim ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.75)',
              background: line.add ? 'rgba(95,212,164,0.06)' : 'transparent',
              borderRadius: 4,
              padding: line.add ? '1px 6px' : '0',
            }}>{line.text}</div>
          ))}

          {/* Approval buttons */}
          {phase.approval && visibleLines.length >= phase.lines.length && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <div style={{
                padding: '4px 14px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                background: 'rgba(95,212,164,0.15)', border: '1px solid rgba(95,212,164,0.4)',
                color: '#5fd4a4', cursor: 'default',
              }}>Approve</div>
              <div style={{
                padding: '4px 14px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.4)', cursor: 'default',
              }}>Edit</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function AgentStreamDemo() {
  const [activePhase, setActivePhase] = useState(0)
  const [donePhases, setDonePhases] = useState([])
  const containerRef = useRef(null)
  const running = useRef(false)

  // Phase durations (ms each phase stays active)
  const DURATIONS = [1400, 1400, 1100, 1000, 1800, 1300, 1800, 1800, 1600, 1700, 2000, 2000, 1000, 1600, 900]

  useEffect(() => {
    if (running.current) return
    running.current = true

    let idx = 0
    const advance = () => {
      if (idx >= PHASES.length) {
        // restart loop
        setTimeout(() => {
          setActivePhase(0)
          setDonePhases([])
          idx = 0
          running.current = false
          running.current = true
          advance()
        }, 3000)
        return
      }
      setActivePhase(idx)
      const duration = DURATIONS[idx] ?? 1400
      setTimeout(() => {
        setDonePhases(prev => [...prev, idx])
        idx++
        advance()
      }, duration)
    }
    advance()
  }, [])

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight
      }
    })
  }

  // Auto-scroll to bottom as new content appears
  useEffect(() => {
    scrollToBottom()
  })

  return (
    <div style={{
      width: '100%', height: '100%',
      background: 'rgba(4,5,14,0.95)',
      borderRadius: 18,
      border: '1px solid rgba(124,110,245,0.25)',
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      boxShadow: '0 0 0 1px rgba(124,110,245,0.08), 0 32px 80px rgba(0,0,0,0.6), 0 0 80px rgba(124,110,245,0.08)',
    }}>
      {/* Titlebar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '11px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.018)',
        flexShrink: 0,
      }}>
        <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#f07070', opacity: 0.9 }} />
        <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#f5a157', opacity: 0.9 }} />
        <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#5fd4a4', opacity: 0.9 }} />
        <span style={{ marginLeft: 10, fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: 'ui-monospace, monospace' }}>
          pulse agent — task manager
        </span>
        <div style={{ flex: 1 }} />
        <div style={{
          padding: '2px 9px', borderRadius: 8, fontSize: 9, fontWeight: 700,
          background: 'rgba(95,212,164,0.15)', border: '1px solid rgba(95,212,164,0.3)',
          color: '#5fd4a4', letterSpacing: '0.08em',
        }}>RUNNING</div>
      </div>

      {/* Goal bar */}
      <div style={{
        padding: '10px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        background: 'rgba(124,110,245,0.05)',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'ui-monospace, monospace' }}>
          $ pulse run "Add a health check endpoint to Task Manager"
        </span>
      </div>

      {/* Phase stream */}
      <div
        ref={containerRef}
        style={{
          flex: 1, overflowY: 'auto', padding: '12px 12px',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(124,110,245,0.2) transparent',
        }}
      >
        {PHASES.map((phase, i) => {
          const isDone = donePhases.includes(i)
          const isActive = activePhase === i && !isDone
          const isVisible = i <= activePhase || isDone
          if (!isVisible) return null
          return (
            <div key={phase.id} data-phase={i}>
              <PhaseCard phase={phase} active={isActive} done={isDone} onLineReveal={scrollToBottom} />
            </div>
          )
        })}
      </div>

      <style>{`
        @keyframes dot-pulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  )
}